import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { Button, Input } from "./ui.tsx";
import { api } from "../api/client.ts";
import { cn } from "@/lib/utils";

/**
 * ═══════════════════════════════════════════════════════════════
 *  修改密码
 * ═══════════════════════════════════════════════════════════════
 *
 * 老板：「密码去哪里修改」——**哪儿也改不了**。
 * `POST /auth/me/password` 这个接口早就写好了，前端一处都没引用过。
 * 「账号安全」页只有登录设备一块，等于这个站的用户永远改不了自己的密码。
 *
 * 🔴 **改密码会把所有会话踢掉，包括他自己这一台**（服务端会清 cookie）。
 *    这是对的 —— 改密码的场景基本只有一个：**号可能被别人拿到了**，
 *    不踢干净的话，那个人手里的 token 照样能用，改了等于没改。
 *    所以提交成功后必须**主动把他送去登录页**：不送的话页面还停在这儿，
 *    他点什么都是 401，会以为系统坏了。
 *
 * ⚠️ 两处版面（手机 / 网页）共用这一个组件 —— 密码规则和"改完要跳登录"
 *    这两件事只能有一份，抄两份迟早有一边忘了跳。
 */
export function ChangePassword({
  /**
   * 三个密码框横着排（网页版用）。
   * ⚠️ 手机上不能横排 —— 一行挤三个密码框，每个只剩一百来像素。
   */
  columns = false,
}: { columns?: boolean } = {}) {
  const navigate = useNavigate();
  const [oldPassword, setOld] = useState("");
  const [newPassword, setNew] = useState("");
  const [confirm, setConfirm] = useState("");
  /** 本地校验的报错。⚠️ 装成 Error 才能跟接口报错用同一个渲染。 */
  const [formError, setFormError] = useState<Error | null>(null);

  const submit = useMutation({
    mutationFn: () => api.post("/auth/me/password", { oldPassword, newPassword }),
    onSuccess: () => {
      /** 🔴 见文件头：服务端已经把 cookie 清了，必须自己走人。 */
      void navigate({ to: "/login", replace: true });
    },
  });

  const go = () => {
    /**
     * ⚠️ 这几条在前端先拦下来，报错才说得准。
     *    全丢给服务端的话，"两次输入不一致"这种问题它根本不知道
     *    （它只收到一个 newPassword），只能回一句笼统的话。
     */
    if (!oldPassword) return setFormError(new Error("请输入当前密码"));
    if (newPassword.length < 8) return setFormError(new Error("新密码至少 8 位"));
    if (newPassword !== confirm) return setFormError(new Error("两次输入的新密码不一致"));
    if (newPassword === oldPassword) return setFormError(new Error("新密码不能和当前密码一样"));
    setFormError(null);
    submit.mutate();
  };

  const error = formError ?? submit.error;

  return (
    <div className="space-y-3">
      <div className={cn(columns ? "grid grid-cols-1 gap-4 md:grid-cols-3" : "space-y-3")}>
      <Input
        label="当前密码"
        type="password"
        autoComplete="current-password"
        value={oldPassword}
        onChange={(e) => {
          setOld(e.target.value);
          setFormError(null);
        }}
      />
      <Input
        label="新密码"
        type="password"
        autoComplete="new-password"
        placeholder="至少 8 位"
        value={newPassword}
        onChange={(e) => {
          setNew(e.target.value);
          setFormError(null);
        }}
      />
      <Input
        label="确认新密码"
        type="password"
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => {
          setConfirm(e.target.value);
          setFormError(null);
        }}
      />
      </div>

      {/*
        ⚠️ **先把后果说出来再让他点。** 改完所有设备都要重新登录，
           包括他手上这一台 —— 不说的话他会以为自己被系统踢了。
      */}
      <p className="text-[12px] leading-relaxed text-muted-foreground">
        改完所有设备都会退出登录（包括这一台），需要用新密码重新登录。
      </p>

      {/* ⚠️ 预留一行高度，出错时按钮不要往下跳（跟登录页同一条规矩）。 */}
      <div className="min-h-[2.5rem]">
        {error && (
          <p className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-[13px] leading-snug text-destructive">
            {error instanceof Error ? error.message : "出了点问题"}
          </p>
        )}
      </div>

      <Button loading={submit.isPending} onClick={go}>
        修改密码
      </Button>
    </div>
  );
}
